import { useCallback, useEffect, useMemo, useRef } from "react";

import type { CustomerRequestConfirmation, GeneralNotesConfirmation } from "../components/SourceReview";
import {
  confirmScope,
  generalScope,
  markScopeChanged,
  requestScope,
  useFactConfirmations,
  type ConfirmationEntries,
} from "../lib/factConfirmations";

interface RequestRef {
  id: string;
}

/** A fact as far as confirming cares: which scope it belongs to. */
export interface ConfirmableFact {
  id: string;
  /** The customer request it is tied to; null or absent for a general note. */
  customerRequestId?: string | null;
}

/**
 * Whether the ticket-level notes show a confirm button. It shows while there is
 * something to confirm: the notes hold a fact, or they changed. A removal leaves the
 * notes changed with no fact, so that case still shows; confirming it clears the
 * change and the button goes. Notes that never held a fact show none.
 */
export function generalNotesConfirmationVisible(input: { hasFacts: boolean; changed: boolean }): boolean {
  return input.hasFacts || input.changed;
}

/**
 * Whether the notes are unconfirmed. With facts, "never confirmed" counts (a new ticket
 * starts unconfirmed); with none, only a change the person made counts, so an empty
 * notes section is never something to confirm.
 */
export function generalNotesDirty(entry: ConfirmationEntries[string] | undefined, hasFacts: boolean): boolean {
  return hasFacts ? !entry?.confirmed : entry !== undefined && !entry.confirmed;
}

/**
 * #820: the confirm step after fact changes. Venus only: Neptune shows no confirm buttons and
 * does not hold the answer back.
 *
 * State is kept in this browser (see lib/factConfirmations.ts), so a fact changed in
 * another app asks nothing of this one. A customer request or the general notes are
 * unconfirmed until the person confirms them, and again after they change a fact in them
 * HERE: through `markFactChanged`, or `markFactAdded` for a fact they add (by hand or by
 * confirming the chat's). A colleague's change, shared live, asks nothing of this person.
 * While anything is unconfirmed the answer stays hidden (`answerHeldBack`).
 */
export function useRequestFactsConfirmation({
  messageId,
  customerRequests,
  facts,
  factsLoaded,
}: {
  messageId: string | null | undefined;
  customerRequests: readonly RequestRef[];
  /** Every fact of the message that is currently shown, with its scope. */
  facts: readonly ConfirmableFact[];
  /** The facts have been fetched: until then an empty list must not read as "no facts yet". */
  factsLoaded: boolean;
}): {
  confirmation: CustomerRequestConfirmation;
  /** Undefined while the notes have nothing to confirm: no facts and no change to take in. */
  generalConfirmation: GeneralNotesConfirmation | undefined;
  answerHeldBack: boolean;
  /** A fact was edited, removed or (de)selected here: its scope needs confirming again. */
  markFactChanged: (factId: string) => void;
  /** A fact is being added here, to this request or (null) to the general notes. */
  markFactAdded: (customerRequestId: string | null) => void;
} {
  const entries = useFactConfirmations();

  const scopeOfFact = useCallback(
    (fact: ConfirmableFact): string | null => {
      if (!messageId) return null;
      return fact.customerRequestId ? requestScope(fact.customerRequestId) : generalScope(messageId);
    },
    [messageId],
  );

  // A fact that shows up while the ticket is open, without `markFactAdded`, was added by
  // someone else: a colleague, shared live. It changes nothing for this person, also not
  // notes that held no fact when the ticket opened (notes holding a fact start "never
  // confirmed"). The first list we see is the baseline: what the ticket arrived with.
  const baseline = useRef<{ messageId: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    if (!messageId || !factsLoaded) return;
    if (baseline.current?.messageId !== messageId) {
      baseline.current = { messageId, ids: new Set(facts.map((fact) => fact.id)) };
      return;
    }
    const seen = baseline.current.ids;
    const notesHeldFact = facts.some((fact) => !fact.customerRequestId && seen.has(fact.id));
    for (const fact of facts) {
      if (seen.has(fact.id)) continue;
      seen.add(fact.id);
      const scope = generalScope(messageId);
      if (!fact.customerRequestId && !notesHeldFact && entries[scope] === undefined) confirmScope(scope);
    }
  }, [messageId, factsLoaded, facts, entries]);

  const markFactChanged = useCallback(
    (factId: string) => {
      // A fact the list does not hold is not ours to judge: it must not flag the notes by default.
      const fact = facts.find((f) => f.id === factId);
      const scope = fact ? scopeOfFact(fact) : null;
      if (scope) markScopeChanged(scope);
    },
    [facts, scopeOfFact],
  );

  const markFactAdded = useCallback(
    (customerRequestId: string | null) => {
      if (!messageId) return;
      markScopeChanged(customerRequestId ? requestScope(customerRequestId) : generalScope(messageId));
    },
    [messageId],
  );

  const dirtyIds = useMemo(
    () => new Set(customerRequests.filter((r) => !entries[requestScope(r.id)]?.confirmed).map((r) => r.id)),
    [customerRequests, entries],
  );
  // The first-time pointer attaches to one button: the first request waiting for
  // confirmation, or the notes' button when only they are.
  const tutorialRequestId = customerRequests.find((r) => dirtyIds.has(r.id))?.id;

  const confirmation = useMemo<CustomerRequestConfirmation>(
    () => ({
      dirtyIds,
      tutorialRequestId,
      onConfirm: (customerRequestId: string) => confirmScope(requestScope(customerRequestId)),
      onUnconfirm: (customerRequestId: string) => markScopeChanged(requestScope(customerRequestId)),
    }),
    [dirtyIds, tutorialRequestId],
  );

  const hasGeneralFacts = facts.some((fact) => !fact.customerRequestId);
  const generalEntry = messageId ? entries[generalScope(messageId)] : undefined;
  const generalDirty = factsLoaded && generalNotesDirty(generalEntry, hasGeneralFacts);
  const generalVisible =
    !!messageId &&
    factsLoaded &&
    generalNotesConfirmationVisible({ hasFacts: hasGeneralFacts, changed: generalDirty });

  const generalConfirmation = useMemo<GeneralNotesConfirmation | undefined>(
    () =>
      generalVisible && messageId
        ? {
            dirty: generalDirty,
            tutorial: generalDirty && tutorialRequestId === undefined,
            onConfirm: () => confirmScope(generalScope(messageId)),
            onUnconfirm: () => markScopeChanged(generalScope(messageId)),
          }
        : undefined,
    [generalVisible, generalDirty, tutorialRequestId, messageId],
  );

  return {
    confirmation,
    generalConfirmation,
    answerHeldBack: dirtyIds.size > 0 || generalDirty,
    markFactChanged,
    markFactAdded,
  };
}
